import axios, { type AxiosResponse } from 'axios'
import BaseService from '../BaseService'

const ACCOMMODATION_API = `${import.meta.env.VITE_NEW_ACCOMMODATIONS_API_URL}`
const GRAPHQL_API = `${import.meta.env.VITE_HOTEL_SEARCH_GRAPHQL_API_URL}`
const GRAPHQL_CLIENT_NAME = 'trivago-business-studio-rate-insights'

// ==================== GraphQL Query ====================

const getPartnerAvatar = `query s($input: AdvertiserDetailsInput!) {
  getAdvertiserDetails(input: $input) {
    advertiserDetails {
      nsid {
        id
        ns
      }
      logo {
        localizedUrlTail
        localizedFaviconUrlTail
      }
      translatedName {
        value
      }
    }
  }
}`

// ==================== Type Definitions ====================

export interface NSID {
  id: number
  ns: number
}

export interface Logo {
  localizedUrlTail: string
  localizedFaviconUrlTail: string
}

export interface TranslatedName {
  value: string
}

export interface AdvertiserDetail {
  nsid: NSID
  logo: Logo
  translatedName: TranslatedName
}

export interface PartnerAvatarResponse {
  data: {
    getAdvertiserDetails: {
      advertiserDetails: AdvertiserDetail[]
    }
  }
}

/** Selected booking-site channel */
export interface Channel {
  id: number
  channelTranslationKey: string
}

/** Raw shape from GET /channel/{accommodationId}/available — a different DTO than the selected-channels endpoint */
export interface AvailableChannelDTO {
  id: number
  rateCount: number
  accommodationCount: number
}

export interface Competitor {
  id: number
  name: string
  address: string
  imageUrl: string
  city: string
  countryCode: string
}

export interface CompetitorSuggestion {
  id: number
  name: string
  imageUrl: string
  countryCode: string
  city: string
  address: string
  reviewRating: number | null
  reviewCount: number | null
}

export interface SelectedChannelsResponse {
  selectedChannels: Channel[]
  partnersData?: {
    advertiserDetails: AdvertiserDetail[]
  }
}

export interface AvailableChannelsResponse {
  bookingSites: AvailableChannelDTO[]
  partnersData?: {
    advertiserDetails: AdvertiserDetail[]
  }
}

export interface AccommodationRequest {
  accommodationId: string
}

export interface RateParams {
  aggregationInterval: 'DAY' | 'WEEK' | 'MONTH'
  ratesType: 'HIGHEST_ALL' | 'LOWEST_ALL' | 'LOWEST_LOCAL'
  currencyCode: string
  startDate: string
  endDate: string
  channels?: number[]
  competitors?: number[]
}

export interface GetRatesRequest {
  accommodationId: string
  params: RateParams
}

export interface UpdateCompetitorsRequest {
  accommodationId: string
  competitors: number[]
}

export interface UpdateBookingSitesRequest {
  accommodationId: string
  bookingSites: number[]
}

export interface PartnerAvatarRequest {
  partnerIds: number[]
}

export interface RateBreakdown {
  singleBreakfast: number
  doubleBreakfast: number
  multiBreakfast: number
  single: number
  double: number
  multi: number
}

export interface RatesByChannel {
  [channelId: string]: RateBreakdown
}

export interface RatesByAccommodation {
  [accommodationId: string]: RatesByChannel
}

export interface RatesResponse {
  [timestamp: string]: RatesByAccommodation
}

// ==================== Service Class ====================

class RateInsightsService extends BaseService {
  async getSelectedChannels({
    accommodationId
  }: AccommodationRequest): Promise<SelectedChannelsResponse> {
    return await axios<Channel[]>({
      url: `${ACCOMMODATION_API}/channel/${accommodationId}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    }).then(async response => {
      const partnerIds: number[] = []
      response.data.map(item => partnerIds.push(item.id))
      const partnersData = await this.getPartnerAvatar({ partnerIds })
      return {
        selectedChannels: response.data,
        partnersData: partnersData?.data?.data?.getAdvertiserDetails,
      }
    })
  }

  getCompetitors({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<Competitor[]>> {
    return axios({
      url: `${ACCOMMODATION_API}/competitor/${accommodationId}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getSuggestions({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<CompetitorSuggestion[]>> {
    return axios({
      url: `${ACCOMMODATION_API}/competitor/${accommodationId}/suggestions`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getRates({ accommodationId, params }: GetRatesRequest): Promise<AxiosResponse<RatesResponse>> {
    return axios({
      url: `${ACCOMMODATION_API}/rate/${accommodationId}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
      params: params,
    })
  }

  updateSelectedCompetitors({
    accommodationId,
    competitors
  }: UpdateCompetitorsRequest): Promise<AxiosResponse<string>> {
    return axios({
      url: `${ACCOMMODATION_API}/competitor/${accommodationId}`,
      method: 'PUT',
      headers: this.studioHeadersWithAttribution(),
      data: { competitorIds: competitors },
    })
  }

  updateBookingSites({
    accommodationId,
    bookingSites
  }: UpdateBookingSitesRequest): Promise<AxiosResponse<Channel[]>> {
    return axios({
      url: `${ACCOMMODATION_API}/channel/${accommodationId}`,
      method: 'PUT',
      headers: this.studioHeadersWithAttribution(),
      data: { channelsIds: bookingSites },
    })
  }

  async getAvailableChannels({
    accommodationId
  }: AccommodationRequest): Promise<AvailableChannelsResponse> {
    return await axios<AvailableChannelDTO[]>({
      url: `${ACCOMMODATION_API}/channel/${accommodationId}/available`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    }).then(async response => {
      const partnerIds: number[] = []
      response.data.map(item => partnerIds.push(item.id))
      const partnersData = await this.getPartnerAvatar({ partnerIds })
      return {
        bookingSites: response.data,
        partnersData: partnersData?.data?.data?.getAdvertiserDetails,
      }
    })
  }

  /** Internal method used by getSelectedChannels and getAvailableChannels */
  private async getPartnerAvatar({ partnerIds }: PartnerAvatarRequest): Promise<AxiosResponse<PartnerAvatarResponse>> {
    const input = partnerIds?.map(item => {
      return { id: item, ns: 400 }
    })

    return await axios({
      url: `${GRAPHQL_API}`,
      method: 'POST',
      data: {
        query: getPartnerAvatar,
        variables: {
          input: { nsids: input },
        },
      },
      headers: this.graphqlHeaders('en-US', GRAPHQL_CLIENT_NAME),
    })
  }
}

export default new RateInsightsService()
